import { createStyles, Theme, Tooltip, withStyles } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { addDays, addMonths, addWeeks, addYears, isAfter, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import * as React from 'react';
import { ViewModeType } from '../../../store/types';
import { DateRangePicker } from './DateRangePicker';
// import FormControl from '@material-ui/core/FormControl';
// import InputLabel from '@material-ui/core/InputLabel';
// import Tooltip from '@material-ui/core/Tooltip';

interface StylesProps {
  classes: {
    select: string;
    tooltipButton: string;
    tooltipSpan: string;
  };
}

export interface CustomControlsProps {
  viewModeType: ViewModeType;
  startDate: Date;
  endDate: Date;
}

export interface CustomControlsActions {
  setViewModeType: (viewModeType: ViewModeType) => void;
  setRange: (startDate: Date, endDate: Date) => void;
  setViewModeTypeAndRange: (startDate: Date, endDate: Date, viewModeType: ViewModeType) => void;
}

type AllProps = StylesProps & CustomControlsProps & CustomControlsActions;

interface CustomItem {
  value: ViewModeType;
  disabled: boolean;
  message: string;
  key: string;
}

export const viewModeDateRangeLimits: {
  [id: string]: (
    startDate: Date,
    endDate: Date,
    dateRangeRef?: Date | boolean
  ) => { disabled: boolean; message: string; minStartDate: Date; maxEndDate: Date };
} = {
  [ViewModeType.Hours]: (startDate: Date, endDate: Date, dateRangeRef: Date | boolean = false) => ({
    disabled: isAfter(endDate, addWeeks(startDate, 2)),
    message: 'Date range cannot be greater than two weeks.',
    minStartDate: dateRangeRef instanceof Date ? addDays(subWeeks(dateRangeRef, 2), 1) : startDate,
    maxEndDate:
      dateRangeRef instanceof Date ? subDays(addWeeks(dateRangeRef, 2), 1) : subDays(addWeeks(startDate, 2), 1)
  }),
  [ViewModeType.Days]: (startDate: Date, endDate: Date, dateRangeRef: Date | boolean = false) => ({
    disabled: isAfter(endDate, addMonths(startDate, 6)),
    message: 'Date range cannot be greater than six months.',
    minStartDate: dateRangeRef instanceof Date ? addDays(subMonths(dateRangeRef, 6), 1) : startDate,
    maxEndDate:
      dateRangeRef instanceof Date ? subDays(addMonths(dateRangeRef, 6), 1) : subDays(addMonths(startDate, 6), 1)
  }),
  [ViewModeType.Weeks]: (startDate: Date, endDate: Date, dateRangeRef: Date | boolean = false) => ({
    disabled: isAfter(endDate, addYears(startDate, 1)),
    message: 'Date range cannot be greater than one year.',
    minStartDate: dateRangeRef instanceof Date ? addDays(subYears(dateRangeRef, 1), 1) : startDate,
    maxEndDate:
      dateRangeRef instanceof Date ? subDays(addYears(dateRangeRef, 1), 1) : subDays(addYears(startDate, 1), 1)
  }),
  [ViewModeType.Months]: (startDate: Date, endDate: Date, dateRangeRef: Date | boolean = false) => ({
    disabled: isAfter(endDate, addYears(startDate, 2)),
    message: 'Date range cannot be greater than two years.',
    minStartDate: dateRangeRef instanceof Date ? addDays(subYears(dateRangeRef, 2), 1) : startDate,
    maxEndDate:
      dateRangeRef instanceof Date ? subDays(addYears(dateRangeRef, 2), 1) : subDays(addYears(startDate, 2), 1)
  })
};

const styles = (theme: Theme) =>
  createStyles({
    select: {
      minWidth: 120
    },
    tooltipButton: {
      marginTop: '4px',
      border: '1px solid #ffffff56',
      backgroundColor: 'transparent',
      color: 'white',
      padding: '3px 7px',
      fontSize: '12px',
      cursor: 'pointer'
    },
    tooltipSpan: {
      fontSize: '12px'
    }
  });

class CustomControls extends React.Component<AllProps> {
  /**
   *
   */
  constructor(props: AllProps) {
    super(props);
    this.valueChanged = this.valueChanged.bind(this);
    this.tooltipResetRangeButtonClick = this.tooltipResetRangeButtonClick.bind(this);
  }

  private getViewModeOptions = (): CustomItem[] =>
    Object.keys(ViewModeType)
      .filter(k => k !== 'None')
      .map(
        (k, i): CustomItem => {
          const { disabled, message } = viewModeDateRangeLimits[k](this.props.startDate, this.props.endDate);
          return {
            value: ViewModeType[k],
            disabled,
            message,
            key: k
          };
        }
      );

  private tooltipResetRangeButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const viewModeType = e.currentTarget.getAttribute('data-key');
    if (!viewModeType) {
      return;
    }
    const vmLimit = viewModeDateRangeLimits[viewModeType](this.props.startDate, this.props.endDate);
    this.props.setViewModeTypeAndRange(this.props.startDate, vmLimit.maxEndDate, ViewModeType[viewModeType]);
  };

  private valueChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const vmType = ViewModeType[e.target.value];

    if (!vmType) {
      e.preventDefault();
      return;
    }

    this.props.setViewModeType(vmType);
  };

  public render() {
    const viewModeTypes = this.getViewModeOptions();

    const dateRangeProps: any = {
      startDate: this.props.startDate,
      endDate: this.props.endDate,
      setRange: this.props.setRange,
      viewModeType: this.props.viewModeType
    };
    return (
      <div className="ap-custom-controls">
        <Select
          className={this.props.classes.select}
          value={this.props.viewModeType}
          onChange={this.valueChanged}
          color="primary"
          inputProps={{
            name: 'View mode',
            id: 'customPreset-viewMode',
            placeholder: 'View mode'
          }}
        >
          {viewModeTypes.map(vm => {
            const menuItem = (
              <MenuItem disabled={vm.disabled} key={vm.value} value={vm.value}>
                {vm.value}
              </MenuItem>
            );
            if (vm.disabled) {
              return (
                <Tooltip
                  title={
                    <div>
                      <span className={this.props.classes.tooltipSpan}>{vm.message}</span>
                      <button
                        data-key={vm.key}
                        className={this.props.classes.tooltipButton}
                        onClick={this.tooltipResetRangeButtonClick}
                      >
                        Reset range
                      </button>
                    </div>
                  }
                  placement="right"
                  interactive
                >
                  <div>{menuItem}</div>
                </Tooltip>
              );
            } else {
              return menuItem;
            }
          })}
        </Select>
        <DateRangePicker {...dateRangeProps} />
      </div>
    );
  }
}

export default withStyles(styles)(CustomControls);



// WEBPACK FOOTER //
// ./src/components/navbar/customControls/CustomControls.tsx